import React, { useMemo } from 'react';
import SearchForm from './SearchForm/SearchForm';
import { useSelector, useDispatch, batch } from 'react-redux';
import { AppState } from 'store';
import SearchResult from './SearchResult/SearchResult';

import { Spin } from 'antd';
import {
  searchExportClear,
  searchDetailsClear,
  searchHistoryClear,
  fetchAsync,
  searchClear,
} from 'pages/Search';
import SearchDetails from 'pages/Search/SearchDetails/SearchDetails';
import { scrollTop } from 'common';
import { createSelector } from 'reselect';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const selector = createSelector(
  (state: AppState) => state.search,
  (state: AppState) => state.searchDetails,
  (state: AppState) => state.searchExport,
  (search, searchDetails, searchExport) => ({
    search,
    searchDetails,
    searchExport,
  }),
);

const Search: React.FC = () => {
  const { search, searchDetails, searchExport } = useSelector(selector);
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const dispatch = useDispatch();

  const clearAll = () => {
    batch(() => {
      dispatch(searchHistoryClear());
      dispatch(searchDetailsClear());
      dispatch(searchExportClear());
      dispatch(searchClear());
    });
  };

  const spinnerTitle = useMemo((): string => {
    if (search.loading) return 'Searching data...';
    if (searchDetails.loading) return 'Searching details...';
    if (searchExport.loading) return 'Exporting data...';
    return '';
  }, [search.loading, searchDetails.loading, searchExport.loading]);

  const handleSearch = (values: any) => {
    clearAll();

    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response: any) => {
        if (response?.accessToken)
          dispatch(fetchAsync(values, response.accessToken));
      });
    }

    scrollTop();
  };

  const getSearchId = () => {
    if (search.propertyData) return search.propertyData.searchId;
    if (search.nameData) return search.nameData.searchId;
    if (search.otherData) return search.otherData.searchId;
    if (search.multiData) return search.multiData.searchId;

    return 0;
  };

  return (
    <Spin
      spinning={search.loading || searchDetails.loading || searchExport.loading}
      size="large"
      tip={spinnerTitle}
    >
      <SearchForm onSubmit={handleSearch} />

      {searchDetails.details && <SearchDetails searchId={getSearchId()} />}

      {(search.propertyData ||
        search.nameData ||
        search.otherData ||
        search.multiData) && <SearchResult onClickDetails={handleSearch} />}
    </Spin>
  );
};

export default React.memo(Search);
